import { Paper, Typography, Box, Grid, useTheme, useMediaQuery } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, XAxis, Bar } from "recharts";
import { FacturaInforme, cuentaComoFactura } from "../Informes";
import { ISimpleUser } from "../../../config/Empleados";
import { formatCurrency } from "../../../../utils/Factura";

interface GraficoProps {
  facturas: FacturaInforme[];
  empleados: ISimpleUser[];
}

const COLORS_SERVICIOS = [
  "#0D47A1",
  "#1B5E20",
  "#E65100",
  "#4A148C",
  "#C62828",
  "#004D40",
  "#455A64",
  "#283593",
  "#5D4037",
  "#37474F",
  "#6A1B9A",
  "#1A237E",
  "#880E4F",
  "#33691E",
  "#795548",
];
const COLORS_METODOS = ["#EF6C00", "#00838F", "#2E7D32", "#6A1B9A", "#F9A825", "#1565C0" ];
const COLOR_BONO = "#3b3b3b";
const COLOR_COMPLEMENTARIAS = "#6b8fa1";
const COLOR_DEFAULT = "#9E9E9E";

const renderLabelInside = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) / 2;
  const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
  const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      fontWeight={700}
      fontSize={12}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(2).replace(".", ",")}%`}
    </text>
  );
};

const GraficoPieDashboard = ({ title, data, nameKey, colors, moneda }: { title: string; data: any[]; nameKey: string; colors: string[]; moneda: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0].payload;
    return (
      <Box sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.300',
        p: 1,
        borderRadius: 1,
        boxShadow: 1,
        minWidth: 120,
        textAlign: 'center'
      }}>
        <Typography variant="subtitle2">{entry[nameKey]}</Typography>
        <Typography variant="h6" fontWeight={600}>
          {formatCurrency(entry.total, moneda)}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Box sx={{ height: 240}}>
        <ResponsiveContainer initialDimension={{ width: 240, height: 240 }} width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="total"
              nameKey={nameKey}
              outerRadius={80}
              label={renderLabelInside}
              labelLine={false}
            >
              {data.map((_, index) => (
                <Cell key={index} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {!isMobile && 
            <Legend
              layout={isTablet ? "vertical" : "horizontal"}
              verticalAlign="bottom"
              align={isTablet ? "right" : "center"}
              height={isTablet ? 200 : 70}
              wrapperStyle={{
                fontSize: 12,
                overflowY: "auto",
                paddingTop: isTablet ? 0 : 12,
              }}
            />}
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const GraficoBarrasDashboard = ({ title, data, nameKey, colors, moneda }: { title: string; data: any[]; nameKey: string; colors: string[]; moneda: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sortedData = [...data].sort((a, b) => b.total - a.total);
  const totalGlobal = sortedData.reduce((acc, item) => acc + item.total, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const entry = payload[0].payload;

    const porcentaje = totalGlobal
    ? (entry.total / totalGlobal) * 100
    : 0;

    return (
      <Box sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'grey.300',
        p: 1,
        borderRadius: 1,
        boxShadow: 1,
        minWidth: 140,
        textAlign: 'center'
      }}>
        <Typography variant="subtitle2">{entry[nameKey]}</Typography>
        <Typography variant="h6" fontWeight={600}>
          {formatCurrency(entry.total, moneda)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`${porcentaje.toFixed(2).replace(".", ",")}%`}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Box sx={{ height: 240 }}>
          <ResponsiveContainer initialDimension={{ width: 240, height: 240 }} width="100%" height="100%">
            <BarChart data={sortedData} margin={{ top: 0, right: 10, left: 20, bottom: 50 }}>
              {!isMobile && <XAxis 
                dataKey={nameKey} 
                tick={{ fontSize: 12 }} 
                interval={0} 
                angle={-45} 
                textAnchor="end" 
              />}
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total">
                {sortedData.map((_, index) => (
                  <Cell key={index} fill={colors[index % colors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
    </Paper>
  );
};

const SeccionPorCategoria = ({ facturasPagadas, empleados, moneda }: { facturasPagadas: FacturaInforme[]; empleados: ISimpleUser[]; moneda: string }) => {
  const facturasEmpleado = Object.values(
    facturasPagadas.reduce((acc, f) => {
      const key = f.empleado;
      if (!acc[key]) acc[key] = { empleado: key, total: 0, numFacturas: 0 };
      acc[key].total += f.total;
      if (cuentaComoFactura(f)) acc[key].numFacturas += 1;
      return acc;
    }, {} as Record<string, { empleado: string; total: number; numFacturas: number }>)
  );

  const facturasServicio = Object.values(
    facturasPagadas.reduce((acc, f) => {
      if (!acc[f.servicio]) acc[f.servicio] = { servicio: f.servicio, total: 0, numFacturas: 0 };
      acc[f.servicio].total += f.total;
      if (cuentaComoFactura(f)) acc[f.servicio].numFacturas += 1;
      return acc;
    }, {} as Record<string, { servicio: string; total: number; numFacturas: number }>)
  );

  const facturasMetodo = Object.values(
    facturasPagadas.reduce((acc, f) => {
      const key = f.metodoPago ?? "Sin pago";
      if (!acc[key]) acc[key] = { metodoPago: key, total: 0, numFacturas: 0 };
      acc[key].total += f.total;
      if (cuentaComoFactura(f)) acc[key].numFacturas += 1;
      return acc;
    }, {} as Record<string, { metodoPago: string; total: number; numFacturas: number }>)
  ).filter(f => f.total > 0);

  const coloresEmpleados = facturasEmpleado.map(fe => {
    if (fe.empleado === "Bonos") return COLOR_BONO;
    if (fe.empleado === "Complementarias") return COLOR_COMPLEMENTARIAS;
    const emp = empleados.find(e => `${e.firstName} ${e.lastName}` === fe.empleado);
    return emp?.color || COLOR_DEFAULT;
  });

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <GraficoPieDashboard
            title="Facturación por empleado"
            data={facturasEmpleado}
            nameKey="empleado"
            colors={coloresEmpleados}
            moneda={moneda}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <GraficoPieDashboard
            title="Facturación por método de pago"
            data={facturasMetodo}
            nameKey="metodoPago"
            colors={COLORS_METODOS}
            moneda={moneda}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <GraficoBarrasDashboard
            title="Facturación por servicio"
            data={facturasServicio}
            nameKey="servicio"
            colors={COLORS_SERVICIOS}
            moneda={moneda}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export const GraficosPorCategoria = ({ facturas, empleados }: GraficoProps) => {
  const facturasPagadas = facturas.filter(f => f.estadoPago === "Pagada");
  const moneda = facturas[0]?.moneda || 'EUR';

  return <SeccionPorCategoria facturasPagadas={facturasPagadas} empleados={empleados} moneda={moneda} />;
};